import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { PageLoad } from '../components/ui';
import { usePrograms } from '../context/ProgramsContext';

function ETIcon({ d, size = 16, stroke = 1.75, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={style}>
      {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
  );
}

const ICONS = {
  doc:      "M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zm0 0v6h6M8 14h8M8 18h6",
  bell:     "M18 16v-5a6 6 0 0 0-12 0v5l-2 2h16zM10 21a2 2 0 0 0 4 0",
  rocket:   "M5 19c0-2 1-4 3-4l8-8 4 4-8 8c0 2-2 3-4 3zm10-13l4 4M7 17l-3 3",
  trophy:   "M8 21h8m-4-4v4M6 4h12v3a6 6 0 0 1-12 0V4zm12 1h3v2a3 3 0 0 1-3 3M6 5H3v2a3 3 0 0 0 3 3",
  pulse:    "M3 12h4l3-8 4 16 3-8h4",
  check:    "M22 11v1a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3",
  alert:    "M12 2L2 22h20zm0 7v5m0 3v.01",
  sparkle:  "M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3zm6 11l1 2.5L21 18l-2 1.5L18 22l-1-2.5L15 18l2-1.5L18 14z",
  building: "M4 21V7l8-4 8 4v14M4 21h16M9 21V11m6 10V11M9 15h6",
  mail:     "M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM3 7l9 6 9-6",
  user:     "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  calendar: "M3 8h18M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM8 2v4m8-4v4",
  send:     "M22 2L11 13M22 2l-7 20-4-9-9-4z",
  external: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3",
  close:    "M6 6l12 12M18 6L6 18",
  grid: (<g><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></g>),
  search: (<g><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></g>),
};

function iconForTrigger(type) {
  if (!type) return ICONS.mail;
  const t = type.toLowerCase();
  if (t.includes('document') || t.includes('doc')) return ICONS.doc;
  if (t.includes('reminder')) return ICONS.bell;
  if (t.includes('launch') || t.includes('countdown')) return ICONS.rocket;
  if (t.includes('goal') || t.includes('congrats') || t.includes('completion')) return ICONS.trophy;
  if (t.includes('urgent') || t.includes('alert')) return ICONS.alert;
  if (t.includes('pulse') || t.includes('weekly')) return ICONS.pulse;
  if (t.includes('checklist') || t.includes('welcome')) return ICONS.check;
  if (t.includes('gosc') || t.includes('resume') || t.includes('career')) return ICONS.sparkle;
  if (t.includes('host') || t.includes('agreement')) return ICONS.building;
  if (t.includes('exit') || t.includes('survey') || t.includes('grading')) return ICONS.user;
  if (t.includes('months') || t.includes('month')) return ICONS.calendar;
  return ICONS.mail;
}

function isAuto(tpl) {
  return tpl.trigger_desc?.toLowerCase().startsWith('auto');
}

function ETHero({ total, sent90d, totalSent, view, setView }) {
  const kpis = [
    { eyebrow: 'Library',      value: total,                         label: 'Active templates' },
    { eyebrow: 'Last 90 days', value: sent90d != null ? sent90d.toLocaleString() : '—', label: 'Emails sent' },
    { eyebrow: 'All time',     value: totalSent != null ? totalSent.toLocaleString() : '—', label: 'Total emails sent' },
  ];
  return (
    <div className="et-hero">
      <div className="et-hero-decor" aria-hidden="true">
        <svg viewBox="0 0 800 300" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
          <defs>
            <pattern id="etgrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M40 0H0V40" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="800" height="300" fill="url(#etgrid)"/>
          <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
            <circle cx="720" cy="60" r="60"/><circle cx="720" cy="60" r="110"/><circle cx="720" cy="60" r="170"/>
          </g>
          <g fill="none" stroke="rgba(245,166,35,0.18)" strokeWidth="1">
            <g transform="translate(555 120) rotate(-8)">
              <rect width="80" height="50" rx="4"/><path d="M0 0l40 30 40-30"/>
            </g>
            <g transform="translate(668 210) rotate(6)">
              <rect width="64" height="40" rx="4"/><path d="M0 0l32 24 32-24"/>
            </g>
          </g>
        </svg>
      </div>
      <div className="et-hero-inner">
        <div className="et-hero-top">
          <div>
            <div className="et-hero-crumb">
              <span>Workspace</span><span className="et-crumb-sep">/</span><span>Email Templates</span>
            </div>
            <h1 className="et-hero-title">Email <span style={{ color: 'var(--amber)' }}>Studio</span></h1>
            <p className="et-hero-lede">Every automated message that goes to students and host partners — previewed in full fidelity, with live triggers and recipient details.</p>
          </div>
          <div className="et-hero-actions">
            <div className="et-view-toggle">
              <button className={view === 'inspector' ? 'active' : ''} onClick={() => setView('inspector')}>
                <ETIcon d={ICONS.doc} size={13}/><span>Inspector</span>
              </button>
              <button className={view === 'gallery' ? 'active' : ''} onClick={() => setView('gallery')}>
                <ETIcon d={ICONS.grid} size={13}/><span>Gallery</span>
              </button>
            </div>
          </div>
        </div>
        <div className="et-hero-stats">
          {kpis.map((k, i) => (
            <div key={i} className="et-stat-card">
              <div className="et-stat-top">
                <span className="et-stat-eyebrow">{k.eyebrow}</span>
              </div>
              <div className="et-stat-num">{k.value}</div>
              <div className="et-stat-label">{k.label}</div>
              <div className="et-stat-bar"><div className="et-stat-bar-fill" style={{ width: '100%' }}/></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ETFilterBar({ tab, setTab, query, setQuery, triggerFilter, setTriggerFilter, counts }) {
  return (
    <div className="et-filterbar">
      <div className="et-tabs">
        {[
          { id: 'all', label: 'All', count: counts.all },
          { id: 'student', label: 'Student', count: counts.student },
          { id: 'host', label: 'Host', count: counts.host },
        ].map(t => (
          <button key={t.id} className={`et-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            <span>{t.label}</span><span className="et-tab-count">{t.count}</span>
          </button>
        ))}
      </div>
      <div className="et-search">
        <ETIcon d={ICONS.search} size={15} style={{ flexShrink: 0 }}/>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search templates, subjects…"/>
        {query && (
          <button className="et-search-clear" onClick={() => setQuery('')}>
            <ETIcon d={ICONS.close} size={13}/>
          </button>
        )}
      </div>
      <div className="et-trigger-toggle">
        {[{ id: 'any', label: 'Any' }, { id: 'auto', label: 'Automated' }, { id: 'manual', label: 'Manual' }].map(o => (
          <button key={o.id} className={triggerFilter === o.id ? 'active' : ''} onClick={() => setTriggerFilter(o.id)}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function ETListRow({ tpl, active, onSelect }) {
  const auto = isAuto(tpl);
  const dot = auto ? '#1F8A5B' : '#7A5AE0';
  return (
    <button className={`et-row${active ? ' active' : ''}`} onClick={() => onSelect(tpl.trigger_type)}>
      <div className="et-row-thumb">
        <span className="et-row-thumb-mark" style={{ color: 'var(--amber)' }}>
          <ETIcon d={iconForTrigger(tpl.trigger_type)} size={14}/>
        </span>
      </div>
      <div className="et-row-body">
        <div className="et-row-title">
          {tpl.label}
          <span className="et-row-trig-dot" style={{ background: dot }}/>
        </div>
        <div className="et-row-subject">{tpl.subject}</div>
        <div className="et-row-meta">
          <span className="et-row-audience">{tpl.audience}</span>
          <span className="et-row-sep">·</span>
          <span>{auto ? 'Automated' : 'Manual'}</span>
        </div>
      </div>
    </button>
  );
}

function ETPreviewStage({ tpl }) {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0.72);

  useEffect(() => {
    function fit() {
      if (!stageRef.current) return;
      const w = stageRef.current.clientWidth;
      const s = Math.min(0.9, Math.max(0.36, (w - 64) / 640));
      setScale(Math.round(s * 100) / 100);
    }
    fit();
    const obs = new ResizeObserver(fit);
    if (stageRef.current) obs.observe(stageRef.current);
    return () => obs.disconnect();
  }, [tpl?.trigger_type]);

  if (!tpl) return null;
  const toAddr = tpl.audience === 'Host'
    ? 'Dr. Emily Johnson <host@example.com>'
    : 'Jane Smith <jane.smith@example.com>';

  return (
    <div className="et-stage" ref={stageRef}>
      <div className="et-window">
        <div className="et-window-bar">
          <div className="et-window-dots">
            <span style={{ background: '#FF5F57' }}/>
            <span style={{ background: '#FEBC2E' }}/>
            <span style={{ background: '#28C840' }}/>
          </div>
          <div className="et-window-url">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1F8A5B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>mail.mcgcollege.com / preview / {tpl.trigger_type}</span>
          </div>
          <div className="et-window-btns">
            <button title="Send test"><ETIcon d={ICONS.send} size={13}/></button>
            <button title="Open full"><ETIcon d={ICONS.external} size={13}/></button>
          </div>
        </div>
        <div className="et-window-fields">
          <div><span className="et-field-lbl">From</span><span>MCG Career Services &lt;careerservices@mcgcollege.com&gt;</span></div>
          <div><span className="et-field-lbl">To</span><span>{toAddr}</span></div>
          <div><span className="et-field-lbl">Subject</span><span className="et-field-strong">{tpl.subject}</span></div>
        </div>
        <div className="et-stage-canvas">
          <div style={{ transformOrigin: 'top center', transform: `scale(${scale})`, width: 640, margin: '0 auto' }}>
            {tpl.html_preview ? (
              <iframe
                key={tpl.trigger_type}
                title="Email preview"
                srcDoc={tpl.html_preview}
                style={{ width: 640, border: 'none', height: 900, display: 'block' }}
                sandbox="allow-same-origin"
              />
            ) : (
              <div style={{ padding: 48, textAlign: 'center', color: '#5C7388', fontSize: 14 }}>No preview available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ETInspector({ tpl }) {
  if (!tpl) return null;
  const auto = isAuto(tpl);
  const dot = auto ? '#1F8A5B' : '#7A5AE0';
  return (
    <aside className="et-inspector">
      <div className="et-insp-section">
        <div className="et-insp-label">Template</div>
        <div className="et-insp-name">{tpl.label}</div>
        <div className="et-insp-code"><code>{tpl.trigger_type}</code></div>
      </div>

      <div className="et-insp-section">
        <div className="et-insp-label">Trigger</div>
        <div className="et-insp-pill">
          <span className="et-insp-dot" style={{ background: dot }}/>
          <span style={{ fontWeight: 700 }}>{auto ? 'Automated' : 'Manual'}</span>
        </div>
        {tpl.trigger_desc && (
          <div className="et-insp-help">
            {tpl.trigger_desc.replace(/^(Auto|Manual):\s*/i, '')}
          </div>
        )}
      </div>

      <div className="et-insp-section">
        <div className="et-insp-label">Audience</div>
        <div className="et-insp-audience">{tpl.audience}</div>
      </div>

      <div className="et-insp-section">
        <div className="et-insp-label">Subject line</div>
        <div className="et-insp-subject">{tpl.subject}</div>
      </div>

    </aside>
  );
}

function ETGalleryCard({ tpl, onSelect }) {
  const auto = isAuto(tpl);
  const dot = auto ? '#1F8A5B' : '#7A5AE0';
  return (
    <div className="et-card" onClick={() => onSelect(tpl.trigger_type)}>
      <div className="et-card-preview">
        <div className="et-card-overlay-tag" style={{ borderColor: dot + '55', color: dot }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0, display: 'inline-block' }}/>
          {tpl.audience}
        </div>
        <div className="et-card-email-wrap">
          {tpl.html_preview ? (
            <iframe
              key={tpl.trigger_type}
              title={tpl.label}
              srcDoc={tpl.html_preview}
              style={{ width: 640, height: 700, border: 'none', display: 'block', pointerEvents: 'none' }}
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="et-card-no-preview">
              <ETIcon d={iconForTrigger(tpl.trigger_type)} size={32} style={{ color: '#8C9DAE' }}/>
            </div>
          )}
        </div>
      </div>
      <div className="et-card-body">
        <div className="et-card-name">{tpl.label}</div>
        <div className="et-card-subject">{tpl.subject}</div>
        <div className="et-card-meta">
          <span className="et-card-dot" style={{ background: dot }}/>
          <span>{auto ? 'Automated' : 'Manual'}</span>
        </div>
      </div>
    </div>
  );
}

export default function EmailTemplatesPage() {
  const { programs: allPrograms, programMap } = usePrograms();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('gallery');
  const [tab, setTab] = useState('all');
  const [programFilter, setProgramFilter] = useState('all'); // 'all' | <code> | 'general'
  const [query, setQuery] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('any');
  const [emailStats, setEmailStats] = useState(null);

  useEffect(() => {
    api.get('/settings/email-templates')
      .then(res => {
        setTemplates(res.data);
        if (res.data.length > 0) setSelected(res.data[0].trigger_type);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));

    api.get('/settings/email-stats')
      .then(res => setEmailStats(res.data))
      .catch(err => console.error(err));
  }, []);

  if (loading) return <PageLoad />;

  const studentTemplates = templates.filter(t => t.audience === 'Student');
  const hostTemplates    = templates.filter(t => t.audience === 'Host');

  const progsOf = t => t.programs || [];

  const filtered = templates.filter(t => {
    if (tab === 'student' && t.audience !== 'Student') return false;
    if (tab === 'host'    && t.audience !== 'Host')    return false;
    if (programFilter === 'general' && progsOf(t).length > 0) return false;
    if (programFilter !== 'all' && programFilter !== 'general' && !progsOf(t).includes(programFilter)) return false;
    if (triggerFilter === 'auto'   && !isAuto(t)) return false;
    if (triggerFilter === 'manual' &&  isAuto(t)) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      const hay = [t.label, t.subject, t.trigger_type, t.trigger_desc || ''].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const counts = { all: templates.length, student: studentTemplates.length, host: hostTemplates.length };

  // Program tabs: only programs that actually have program-specific templates, in the app's order.
  const programCodes = (allPrograms || [])
    .map(p => p.code)
    .filter(code => templates.some(t => progsOf(t).includes(code)));
  const progCount = code => templates.filter(t => progsOf(t).includes(code)).length;
  const generalCount = templates.filter(t => progsOf(t).length === 0).length;
  const effectiveSelected = filtered.find(t => t.trigger_type === selected) || filtered[0] || null;

  return (
    <div className="et-page">
      <ETHero
        total={templates.length}
        sent90d={emailStats?.sent_90d ?? null}
        totalSent={emailStats?.total_all_time ?? null}
        view={view}
        setView={setView}
      />

      <div className="et-content">
        <ETFilterBar
          tab={tab} setTab={setTab}
          query={query} setQuery={setQuery}
          triggerFilter={triggerFilter} setTriggerFilter={setTriggerFilter}
          counts={counts}
        />

        {/* Program sections — see each program's emails (auto + manual), plus General */}
        <div className="et-program-tabs">
          <span className="et-program-tabs-label">Program</span>
          <button className={`et-ptab${programFilter === 'all' ? ' active' : ''}`} onClick={() => setProgramFilter('all')}>
            All programs
          </button>
          {programCodes.map(code => (
            <button
              key={code}
              className={`et-ptab${programFilter === code ? ' active' : ''}`}
              title={programMap[code] || code}
              onClick={() => setProgramFilter(code)}
            >
              {code} <span className="et-ptab-count">{progCount(code)}</span>
            </button>
          ))}
          <button className={`et-ptab${programFilter === 'general' ? ' active' : ''}`} onClick={() => setProgramFilter('general')}>
            General <span className="et-ptab-count">{generalCount}</span>
          </button>
        </div>

        {view === 'inspector' ? (
          <div className="et-studio">
            <div className="et-list">
              {filtered.length === 0 ? (
                <div className="et-empty">No templates match your filters.</div>
              ) : filtered.map(tpl => (
                <ETListRow
                  key={tpl.trigger_type}
                  tpl={tpl}
                  active={tpl.trigger_type === effectiveSelected?.trigger_type}
                  onSelect={setSelected}
                />
              ))}
            </div>
            <ETPreviewStage tpl={effectiveSelected}/>
            <ETInspector tpl={effectiveSelected}/>
          </div>
        ) : (
          <div className="et-gallery">
            {filtered.map(tpl => (
              <ETGalleryCard
                key={tpl.trigger_type}
                tpl={tpl}
                onSelect={t => { setSelected(t); setView('inspector'); }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
